import { forwardRef } from "react";
import { FileText, Paperclip } from "@semo/icons";
import { cn, Skeleton, Button } from "@semo/shared-ui";
import type { ChatQuoteCardProps } from "../types";
import { MessageCardWrapper } from "../layout/MessageCardWrapper";

/**
 * QuoteCard - 채팅 내 견적서 카드
 *
 * 견적 정보를 요약하여 카드 형태로 표시합니다.
 * 역할(Buyer/Vendor)에 따라 다른 액션 버튼을 제공합니다.
 */
export const QuoteCard = forwardRef<HTMLDivElement, ChatQuoteCardProps>(
  (
    {
      message,
      role,
      sender,
      onViewQuote,
      onContract,
      onEditQuote,
      isLatestQuote,
      contractId,
      hasPendingCashDeposit,
    },
    ref,
  ) => {
    const { quoteData, createdAt, isPending } = message;

    // Show skeleton UI while loading
    if (isPending) {
      return (
        <MessageCardWrapper
          ref={ref}
          data-slot="chat-quote-card"
          className={cn(
            "bg-white ",
            "border rounded-xl p-3 sm:p-4 shadow-sm",
            sender === "me" ? "border-primary/30 " : "border-slate-200 ",
          )}
        >
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 pb-2 sm:pb-3 border-b border-slate-100 ">
            <Skeleton className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-24" />
            </div>
            <Skeleton className="h-3 w-12" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-6 w-32" />
          </div>
          <div className="flex gap-2 mt-3">
            <Skeleton className="flex-1 h-9 rounded-lg" />
            <Skeleton className="flex-1 h-9 rounded-lg" />
          </div>
        </MessageCardWrapper>
      );
    }

    if (!quoteData) return null;

    const time = new Date(createdAt).toLocaleTimeString("ko-KR", {
      hour: "2-digit",
      minute: "2-digit",
    });

    // The room-level active contract state is authoritative. Quote payloads
    // are historical and may still contain the ID of a cancelled contract.
    const effectiveContractId = contractId;

    // Role에 따른 버튼 텍스트
    const viewButtonText = role === "buyer" ? "견적서 상세보기" : "견적서 확인";

    // Action button text logic
    let actionButtonText = "";
    let isActionDisabled = false;

    if (role === "buyer") {
      if (effectiveContractId) {
        if (isLatestQuote) {
          // ✅ 계약 완료 & 최신 견적 -> 계약서 보기
          actionButtonText = "계약서 보기";
          isActionDisabled = false;
        } else {
          // ✅ 계약 완료 & 지난 견적 -> 계약 마감 (선택 불가)
          actionButtonText = "계약 마감";
          isActionDisabled = true;
        }
      } else {
        // ✅ 계약 전
        if (isLatestQuote) {
          if (hasPendingCashDeposit) {
            // 이미 입금 대기 중인 결제건이 있다 — 누르면 서버가 거부할 것을
            // 미리 막는다(기존엔 눌러야만 "이미 진행 중인..." 토스트로 알았다).
            actionButtonText = "입금 대기 중";
            isActionDisabled = true;
          } else {
            actionButtonText = "계약하기";
            isActionDisabled = false;
          }
        } else {
          actionButtonText = "만료된 견적";
          isActionDisabled = true;
        }
      }
    } else {
      // Vendor role
      if (effectiveContractId) {
        if (isLatestQuote) {
          // ✅ 계약 완료 & 최신 견적 -> 계약서 보기
          actionButtonText = "계약서 보기";
          isActionDisabled = false;
        } else {
          // ✅ 계약 완료 & 지난 견적 -> 계약 마감
          actionButtonText = "계약 마감";
          isActionDisabled = true;
        }
      } else {
        if (isLatestQuote) {
          actionButtonText = "견적서 수정";
          isActionDisabled = false;
        } else {
          actionButtonText = "이 견적으로 작성";
          isActionDisabled = false; // Allow vendors to use old quotes as templates
        }
      }
    }

    const actionHandler = effectiveContractId
      ? onContract
      : role === "buyer"
        ? onContract
        : onEditQuote;

    // Styles based on active status
    const isOutdated = !isLatestQuote;
    const cardBgClass = isOutdated ? "bg-slate-50 " : "bg-white ";
    const borderColorClass = isOutdated
      ? "border-slate-200 "
      : sender === "me"
        ? "border-primary/30 "
        : "border-slate-200 ";
    const textColorClass = isOutdated ? "text-slate-500 " : "text-slate-900 ";

    return (
      <MessageCardWrapper
        ref={ref}
        data-slot="chat-quote-card"
        className={cn(
          cardBgClass,
          borderColorClass,
          "border rounded-xl p-3 sm:p-4 shadow-sm transition-colors",
        )}
      >
        {/* Header */}
        <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 pb-2 sm:pb-3 border-b border-slate-100 ">
          <div
            className={cn(
              "w-8 h-8 sm:w-10 sm:h-10 rounded-lg",
              "flex items-center justify-center shrink-0",
              isOutdated
                ? "bg-slate-100  text-slate-400 "
                : "bg-primary/5  text-primary-text ",
            )}
          >
            <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  "text-sm sm:text-sm font-semibold truncate",
                  textColorClass,
                )}
              >
                견적서{" "}
                {quoteData.versionNumber && `v${quoteData.versionNumber}`}
              </div>
              {isOutdated && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-2xs font-medium bg-slate-100  text-slate-500  border border-slate-200 ">
                  만료됨
                </span>
              )}
            </div>
          </div>
          <div className="text-2xs sm:text-2xs text-slate-400  shrink-0">
            {time}
          </div>
        </div>

        {/* Amount */}
        <div className="flex items-center justify-between text-xs sm:text-sm mb-2 sm:mb-0">
          <span className="text-slate-500 ">제안 금액</span>
          <span
            className={cn(
              "font-semibold text-base sm:text-base",
              isOutdated
                ? "text-slate-500  decoration-slate-400"
                : "text-[#2563eb] ",
            )}
          >
            {typeof quoteData.totalAmount === "number"
              ? quoteData.totalAmount.toLocaleString("ko-KR")
              : quoteData.totalAmount}
            원
          </span>
        </div>

        {/* Quote Items */}
        {quoteData.items &&
          Array.isArray(quoteData.items) &&
          quoteData.items.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100 ">
              <div className="text-2xs font-medium text-slate-500  mb-2">
                견적 항목 ({quoteData.items.length})
              </div>
              <div className="space-y-1.5">
                {quoteData.items.map((item, idx: number) => (
                  <div
                    key={idx}
                    className={cn(
                      "flex items-center gap-2",
                      "text-2xs sm:text-xs",
                      isOutdated ? "text-slate-400 " : "text-slate-600 ",
                      "bg-slate-50  p-2 rounded-lg",
                    )}
                  >
                    <span className="truncate flex-1">
                      {item.name || item.notes}
                    </span>
                    <span className="text-2xs text-slate-400">
                      {item.amount?.toLocaleString("ko-KR")}원
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Attachments */}
        {quoteData.attachments &&
          Array.isArray(quoteData.attachments) &&
          quoteData.attachments.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100 ">
              <div className="text-2xs font-medium text-slate-500  mb-2">
                첨부파일 ({quoteData.attachments.length})
              </div>
              <div className="space-y-1.5">
                {quoteData.attachments.map((file, idx) => (
                  <a
                    key={idx}
                    href={file.url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      "flex items-center gap-2",
                      "text-2xs sm:text-xs",
                      isOutdated ? "text-slate-400 " : "text-slate-600 ",
                      "bg-slate-50  p-2 rounded-lg hover:bg-slate-100 transition-colors",
                    )}
                  >
                    <Paperclip className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate flex-1 underline decoration-slate-300 underline-offset-2">
                      {file.fileName || "첨부파일"}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          )}

        {/* Action Buttons */}
        <div className="flex gap-2 mt-2 sm:mt-3">
          <Button
            onClick={onViewQuote}
            variant="outline"
            className="flex-1 h-auto py-2 text-xs sm:text-sm font-semibold"
          >
            {viewButtonText}
          </Button>
          {actionHandler && (
            <Button
              onClick={actionHandler}
              disabled={isActionDisabled}
              variant={
                isActionDisabled
                  ? "secondary"
                  : effectiveContractId
                    ? "default" // Contracted -> Primary
                    : role === "supplier" && isOutdated
                      ? "outline" // Old quote (Vendor) -> Outline
                      : "default" // Latest quote -> Primary
              }
              className={cn(
                "flex-1 h-auto py-2 text-xs sm:text-sm font-semibold",
              )}
            >
              {actionButtonText}
            </Button>
          )}
        </div>
      </MessageCardWrapper>
    );
  },
);

QuoteCard.displayName = "QuoteCard";
